// Typed event bus = the intent layer between Phaser Scenes and the service
// layer. CLAUDE.md: Scenes must NOT call backend/sim directly — they emit
// typed intents; a controller (service) applies them and emits state updates.

import type { RestKind, Weapon } from '../sim/JKGame_types'

export type JKGameBusEvents = {
  // Player intents (Scene -> service)
  'intent:move': { zone: number } // enter an adjacent room (maze transition)
  'intent:beginCombat': { targetId: string } // walked into a bot → start a fight
  'intent:attack': { targetId: string }
  'intent:special': { targetId: string }
  'intent:flee': Record<string, never>
  'intent:escape': Record<string, never>
  'intent:rest': { kind: RestKind }
  'intent:pickupWeapon': { weapon: Weapon }
  'intent:pickupItem': { item: string }
  'intent:chooseBoon': { boonId: string } // pick one of the three offered run buffs

  // State signals (service -> Scene)
  'match:updated': Record<string, never>
  'match:over': { winnerId: string | null; reason: 'survivor' | 'escape' | 'dead' }
}

type Handler<T> = (payload: T) => void

class GameEventBus {
  private readonly handlers = new Map<keyof JKGameBusEvents, Set<Handler<unknown>>>()

  on<K extends keyof JKGameBusEvents>(event: K, handler: Handler<JKGameBusEvents[K]>): () => void {
    let set = this.handlers.get(event)
    if (!set) {
      set = new Set()
      this.handlers.set(event, set)
    }
    set.add(handler as Handler<unknown>)
    return () => this.off(event, handler)
  }

  off<K extends keyof JKGameBusEvents>(event: K, handler: Handler<JKGameBusEvents[K]>): void {
    this.handlers.get(event)?.delete(handler as Handler<unknown>)
  }

  emit<K extends keyof JKGameBusEvents>(event: K, payload: JKGameBusEvents[K]): void {
    const set = this.handlers.get(event)
    if (!set) return
    for (const handler of [...set]) (handler as Handler<JKGameBusEvents[K]>)(payload)
  }

  clear(): void {
    this.handlers.clear()
  }
}

// Single shared bus for the slice.
export const gameBus = new GameEventBus()
